// Centralised help content registry, keyed by route path.
// Each entry maps to the structured help data displayed in the HelpDrawer.

export interface HelpKeyFeature {
  label: string;
  detail: string;
}

export interface HelpSectionField {
  name: string;
  description: string;
}

export interface HelpStepItem {
  text?: string;
  note?: string;
  tip?: string;
  subSteps?: string[];
}

export interface HelpStep {
  title: string;
  items: HelpStepItem[];
}

export interface HelpFaqItem {
  question: string;
  answer: string;
  steps?: string[];
}

export interface HelpRelatedPage {
  label: string;
  path: string;
}

export interface HelpContent {
  pageTitle: string;
  description: string;
  icon?: string;
  functionality: {
    overview: string;
    keyFeatures: HelpKeyFeature[];
    sectionsAndFields: HelpSectionField[];
    useCases: string[];
  };
  userGuide: {
    gettingStarted: string;
    steps: HelpStep[];
    proTips: string[];
  };
  faq: HelpFaqItem[];
  relatedPages: HelpRelatedPage[];
}

export const helpContent: Record<string, HelpContent> = {
  '/': {
    pageTitle: 'Homepage',
    description: 'Your personal overview of timesheet status, pending actions, and team activity.',
    icon: '🏠',
    functionality: {
      overview:
        'The Homepage is the first screen you see after logging in. It gives you a snapshot of your current timesheet, any items requiring your attention, and — if you are a manager — a summary of your team\'s activity.',
      keyFeatures: [
        { label: 'Timesheet Summary', detail: 'Shows your current week\'s hours at a glance without navigating to the full timesheet.' },
        { label: 'Pending Approvals', detail: 'Highlights timesheets waiting for your review so you can act on them immediately.' },
        { label: 'Team Activity', detail: 'Displays recent submissions and status changes across your team (managers and admins only).' },
        { label: 'Quick Entry', detail: 'Lets you log time against a project directly from the homepage without opening the full timesheet grid.' },
        { label: 'Notifications', detail: 'Surfaces alerts such as overdue timesheets, rejection notices, and upcoming deadlines.' },
      ],
      sectionsAndFields: [
        { name: 'My Timesheet', description: 'A summary card showing your logged hours for the current week and submission status.' },
        { name: 'Pending Approvals', description: 'A list of timesheets submitted by your direct reports that are awaiting your approval.' },
        { name: 'Team Activity', description: 'A feed of recent timesheet events across your team, such as submissions and approvals.' },
        { name: 'Quick Entry', description: 'A compact form for logging time against a project without leaving the homepage.' },
      ],
      useCases: [
        'Checking whether you have submitted your timesheet for the current week.',
        'Approving a team member\'s timesheet without navigating to the full Approvals screen.',
        'Getting a quick read on team activity first thing in the morning.',
        'Logging a small number of hours quickly without opening the full timesheet grid.',
      ],
    },
    userGuide: {
      gettingStarted:
        'The Homepage loads automatically when you log in. If you are a new user, complete the Welcome Tour first — it covers the key areas of the application in under 60 seconds.',
      steps: [
        {
          title: 'How do I submit my timesheet from the homepage?',
          items: [
            { text: 'Locate the My Timesheet card on the homepage.' },
            { text: 'Click View Timesheet to open the full timesheet grid.', tip: 'If your timesheet is already complete, the Submit button may appear directly on the summary card.' },
            { text: 'Review your entries and click Submit for Approval.' },
          ],
        },
        {
          title: 'How do I approve a team member\'s timesheet?',
          items: [
            { text: 'Find the Pending Approvals section on the homepage.', note: 'This section is only visible to users with manager or admin permissions.' },
            { text: 'Click the timesheet entry you want to review.' },
            { text: 'Click Approve or Reject and add a comment if required.' },
          ],
        },
        {
          title: 'How do I log time quickly from the homepage?',
          items: [
            { text: 'Click the Quick Entry button on the homepage.' },
            { text: 'Select the project by typing the first few characters of the project name.' },
            { text: 'Enter the number of hours and select the date.' },
            { text: 'Click Save to record the entry.', note: 'Quick Entry adds to your existing timesheet. It does not replace or overwrite existing entries for the same project and date.' },
          ],
        },
      ],
      proTips: [
        'Pin your most-used projects in Settings so they appear first in the Quick Entry project list.',
        'The homepage approval list shows only the most recent five submissions — go to the full Approvals screen to see all pending items.',
        'If your timesheet card shows a warning icon, it means the submission deadline is approaching.',
        'Use the keyboard shortcut ? to open the Help drawer from anywhere in the application.',
      ],
    },
    faq: [
      {
        question: 'What does the homepage show if I have no pending actions?',
        answer: 'If you have no pending approvals and your timesheet is already submitted, the homepage shows your submitted hours and a confirmation status. No action is required.',
      },
      {
        question: 'How do I see more than the five most recent team activities?',
        answer: 'The homepage shows a preview only. Navigate to Reports → Timesheet Reports to see the full activity history for your team.',
      },
      {
        question: 'Can I customise what appears on my homepage?',
        answer: 'Homepage layout customisation is not currently available. The sections displayed depend on your role — employees see their timesheet summary, managers also see team approvals and activity.',
      },
      {
        question: 'Why can\'t I see the Pending Approvals section?',
        answer: 'The Pending Approvals section is only shown to users with a manager or admin role. If you believe your role is incorrect, contact your system administrator.',
      },
    ],
    relatedPages: [
      { label: 'My Timesheet', path: '/timesheet' },
      { label: 'Approvals', path: '/approvals' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
    ],
  },

  '/manager': {
    pageTitle: 'Manager View',
    description: 'An overview of your team\'s timesheet status, pending approvals, and submission activity.',
    icon: '👔',
    functionality: {
      overview:
        'The Manager View is the homepage for users with a manager or admin role. It gives you a consolidated picture of your team\'s timesheet activity — who has submitted, who is overdue, what is pending your approval, and how team hours are tracking against targets for the current period.',
      keyFeatures: [
        { label: 'Team Submission Status', detail: 'Shows at a glance which team members have submitted, are in draft, or are overdue for the current week.' },
        { label: 'Pending Approvals', detail: 'Lists all timesheets awaiting your review so you can approve or reject directly from this screen.' },
        { label: 'Team Hours Summary', detail: 'Displays total hours logged across the team for the current period, broken down by member.' },
        { label: 'Overdue Alerts', detail: 'Highlights team members who have not submitted their timesheet by the expected deadline.' },
        { label: 'Approval History', detail: 'Shows a recent log of approvals and rejections you have processed, so you have a clear audit trail.' },
        { label: 'Direct Navigation', detail: 'Click any team member\'s entry to jump directly to their full timesheet for detailed review.' },
      ],
      sectionsAndFields: [
        { name: 'Team Status Overview', description: 'A summary panel showing how many team members have submitted, are in draft, or are overdue for the current week.' },
        { name: 'Pending Approvals', description: 'A list of timesheets submitted by your direct reports that are waiting for your approval or rejection.' },
        { name: 'Team Member List', description: 'A row-per-person breakdown of submission status, total hours logged, and the date of their last submission.' },
        { name: 'Overdue Section', description: 'Highlights any team members who have missed the submission deadline, sorted by how many days overdue.' },
        { name: 'Recent Activity Feed', description: 'A chronological log of recent timesheet events across your team — submissions, approvals, and rejections.' },
      ],
      useCases: [
        'Reviewing which team members still need to submit their timesheets before the weekly deadline.',
        'Approving or rejecting a batch of submitted timesheets at the start of the day.',
        'Identifying team members who are consistently late with submissions.',
        'Getting a quick read on total team hours before a client billing run.',
        'Jumping to a specific team member\'s timesheet to investigate an unusual entry.',
      ],
    },
    userGuide: {
      gettingStarted:
        'The Manager View loads automatically when you switch to Manager mode using the persona toggle in the top navigation. You must have a manager or admin role assigned to your account before the toggle becomes available.',
      steps: [
        {
          title: 'How do I switch to Manager View?',
          items: [
            { text: 'Locate the persona toggle in the top navigation bar.' },
            { text: 'Click Manager to switch from your personal employee view to the Manager View.', note: 'The toggle only appears if your account has been assigned a manager or admin role. Contact your system administrator if you believe you should have access.' },
            { text: 'The page reloads with team data replacing your personal timesheet summary.' },
          ],
        },
        {
          title: 'How do I approve a team member\'s timesheet?',
          items: [
            { text: 'Locate the Pending Approvals section on the Manager View.' },
            { text: 'Click the team member\'s name or the timesheet row to open the detail view.' },
            { text: 'Review the hours and project allocations.' },
            { text: 'Click Approve to confirm, or Reject to decline.', tip: 'Add a rejection reason in the comment field — this is sent directly to the team member so they know what to correct.' },
            { note: 'Approved timesheets are locked for editing by the employee. If a correction is needed after approval, you must unlock the timesheet from the Approvals screen.' },
          ],
        },
        {
          title: 'How do I see which team members are overdue?',
          items: [
            { text: 'Check the Overdue section on the Manager View — it lists anyone who has not submitted by the current deadline.' },
            { text: 'Click a team member\'s name to open their timesheet and see their current draft or empty entries.', tip: 'Use the Missing Timesheets report for a full historical view of overdue submissions across all periods.' },
          ],
        },
        {
          title: 'How do I view a specific team member\'s full timesheet?',
          items: [
            { text: 'Find the team member in the Team Member List or Pending Approvals section.' },
            { text: 'Click their name or the arrow icon to navigate directly to their timesheet.' },
            { text: 'Review their entries in the full timesheet grid.', note: 'You are viewing the timesheet in read-only mode unless you explicitly unlock it for editing.' },
          ],
        },
        {
          title: 'How do I reject a timesheet and request changes?',
          items: [
            { text: 'Open the timesheet from the Pending Approvals section.' },
            { text: 'Click Reject.' },
            { text: 'Type your reason in the comment field — be specific about what needs to be corrected.' },
            { text: 'Click Confirm Rejection.', note: 'The team member receives a notification with your comment and the timesheet is returned to draft status for them to edit and resubmit.' },
          ],
        },
      ],
      proTips: [
        'Approve timesheets in bulk from the Approvals screen when you have a large team — it is faster than reviewing each one individually from the Manager View.',
        'The overdue list is sorted by days overdue, not alphabetically — the most urgent cases always appear at the top.',
        'If a team member\'s hours look unusual, click through to their full timesheet before approving — the summary view only shows totals, not the project breakdown.',
        'Switch back to your personal Employee View before logging your own time — entries made in Manager View may be attributed incorrectly.',
      ],
    },
    faq: [
      {
        question: 'How do I access the Manager View?',
        answer: 'Use the persona toggle in the top navigation bar to switch between Employee and Manager views. The toggle only appears if your account has a manager or admin role.',
      },
      {
        question: 'Can I approve multiple timesheets at once?',
        answer: 'Bulk approval is available on the full Approvals screen. The Manager View shows individual approval actions — navigate to Approvals for bulk processing.',
        steps: [
          'Go to the Approvals screen from the left navigation.',
          'Select the checkboxes next to the timesheets you want to approve.',
          'Click Approve Selected.',
        ],
      },
      {
        question: 'What does the submission status colour coding mean?',
        answer: 'Green means submitted and approved, amber means submitted and awaiting your review, grey means saved as draft but not yet submitted, and red means overdue with no submission.',
      },
      {
        question: 'Can I edit a team member\'s timesheet myself?',
        answer: 'Managers cannot directly edit a team member\'s timesheet. You can reject it with a comment so the employee corrects and resubmits, or contact your system administrator to make a correction on your behalf.',
      },
      {
        question: 'Why is a team member missing from my Manager View?',
        answer: 'The Manager View only shows direct reports assigned to your account. If a team member is missing, their reporting line may not be configured correctly — contact your system administrator to update the team structure.',
      },
      {
        question: 'How do I see timesheets from previous weeks?',
        answer: 'The Manager View defaults to the current week. Use the Timesheet Reports screen to view team submissions for any previous period.',
      },
      {
        question: 'What does "overdue" mean on the Manager View?',
        answer: 'A timesheet is marked overdue when a team member has not submitted it by the deadline configured for your organisation. The number of days overdue is shown next to their name.',
      },
      {
        question: 'Can I unlock a timesheet I have already approved?',
        answer: 'Yes. Go to the Approvals screen, find the approved timesheet, and use the Unlock option to return it to draft status for the employee to edit.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'Missing Timesheets', path: '/reports/missing' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Team Members', path: '/team' },
    ],
  },

  '/timesheet': {
    pageTitle: 'My Timesheet',
    description: 'Enter, edit, and submit your weekly time entries against projects and cost centres.',
    icon: '⏱️',
    functionality: {
      overview:
        'The My Timesheet page is where you record your working hours for each week. You can add projects, enter daily hours against them, save drafts, and submit your completed timesheet for manager approval.',
      keyFeatures: [
        { label: 'Weekly Grid', detail: 'Displays a row per project with a column for each day of the week, so you can enter hours efficiently in one view.' },
        { label: 'Add Row', detail: 'Adds a new project row to the grid so you can track time across multiple projects in the same week.' },
        { label: 'Submit for Approval', detail: 'Sends your completed timesheet to your manager for review and sign-off.' },
        { label: 'Save as Draft', detail: 'Saves your progress without submitting, allowing you to return and complete it later.' },
        { label: 'Week Navigation', detail: 'Lets you move backwards and forwards between weeks to view or edit historical and future timesheets.' },
        { label: 'Recall', detail: 'Retrieves a submitted timesheet so you can make corrections before it is approved.' },
      ],
      sectionsAndFields: [
        { name: 'Week Selector', description: 'Displays the currently selected week and allows navigation to previous or future weeks.' },
        { name: 'Timesheet Grid', description: 'A table with one row per project and columns for each day of the week. Click a cell to enter hours.' },
        { name: 'Project Column', description: 'Shows the name of the project or cost centre the row is associated with.' },
        { name: 'Day Columns (Mon–Sun)', description: 'Individual cells where you enter the number of hours worked on that project for each day.' },
        { name: 'Total Column', description: 'Automatically calculates the total hours for that project row across the selected week.' },
        { name: 'Add Row Button', description: 'Opens a project search to add a new project row to the timesheet grid.' },
        { name: 'Submit Button', description: 'Submits the timesheet for approval. Only available when the timesheet is in Draft or Not Started status.' },
        { name: 'Recall Button', description: 'Returns a submitted timesheet to Draft status so you can edit it. Only available when status is Submitted.' },
        { name: 'Status Badge', description: 'Shows the current state of the timesheet: Not Started, Draft, Submitted, Approved, or Rejected.' },
      ],
      useCases: [
        'Recording daily hours across multiple projects at the end of each day or week.',
        'Correcting an error in a submitted timesheet by recalling it before the manager approves it.',
        'Reviewing what you logged in a previous week.',
        'Checking how many hours remain before you hit your weekly limit.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to My Timesheet from the left menu. Before you can enter time, at least one project must be available in the system — contact your administrator if no projects appear in the project search.',
      steps: [
        {
          title: 'How do I add a project to my timesheet?',
          items: [
            { text: 'Click the Add Row button below the timesheet grid.' },
            { text: 'Type the first few characters of the project name in the search field.' },
            { text: 'Select the correct project from the dropdown results.', note: 'If a project does not appear in the search, it may be inactive or you may not have permission to log against it. Contact your administrator.' },
            { text: 'The project row appears in the grid, ready for hour entry.' },
          ],
        },
        {
          title: 'How do I enter hours?',
          items: [
            { text: 'Click the cell in the grid that corresponds to the project row and the correct day column.' },
            { text: 'Type the number of hours (decimals are supported, e.g. 1.5 for 90 minutes).', tip: 'Press Tab to move to the next day cell without using the mouse.' },
            { text: 'Click outside the cell or press Enter to confirm the entry.' },
          ],
        },
        {
          title: 'How do I submit my timesheet?',
          items: [
            { text: 'Ensure all your hours for the week are entered.' },
            { text: 'Click the Submit for Approval button.', note: 'Once submitted, you cannot edit the timesheet unless you recall it or your manager rejects it.' },
            { text: 'The status badge changes to Submitted and your manager receives a notification.' },
          ],
        },
        {
          title: 'How do I recall a submitted timesheet?',
          items: [
            { text: 'Navigate to the week containing the submitted timesheet.' },
            { text: 'Click the Recall button.', note: 'Recall is only available while the timesheet is in Submitted status. Once a manager has approved it, recall is no longer possible.' },
            { text: 'The timesheet returns to Draft status and you can edit it.' },
            { text: 'Re-submit when your changes are complete.' },
          ],
        },
        {
          title: 'How do I view a previous week\'s timesheet?',
          items: [
            { text: 'Click the left arrow next to the week selector at the top of the page.' },
            { text: 'Continue clicking until you reach the week you want to view.' },
            { tip: 'You can also click directly on the week date to open a date picker and jump to a specific week.' },
          ],
        },
      ],
      proTips: [
        'Use Tab to move between day cells quickly — it is faster than clicking each cell.',
        'Save as Draft regularly if you are entering hours across multiple sessions so you do not lose progress.',
        'If your timesheet is rejected, check the rejection comment from your manager before making changes — it will explain what needs correcting.',
        'You can enter 0 in a cell to explicitly mark a day as non-working without deleting the project row.',
      ],
    },
    faq: [
      {
        question: 'Can I enter time in half-hour increments?',
        answer: 'Yes. Enter decimals in any hour cell — for example, 0.5 for 30 minutes or 1.5 for 90 minutes.',
      },
      {
        question: 'What does the Rejected status mean?',
        answer: 'Your manager has reviewed your timesheet and requested changes. Open the timesheet, read the rejection comment, make the necessary corrections, and re-submit.',
      },
      {
        question: 'How do I delete a project row I added by mistake?',
        answer: 'Click the row options menu (three dots) on the right side of the project row and select Remove Row.',
        steps: ['Click the three-dot menu on the project row.', 'Select Remove Row.', 'Confirm the deletion if prompted.'],
      },
      {
        question: 'Why is the Submit button greyed out?',
        answer: 'The Submit button is disabled when the timesheet is already in Submitted or Approved status, or when no hours have been entered. Check the status badge and ensure at least one hour is recorded before submitting.',
      },
      {
        question: 'Can I copy last week\'s timesheet to save time?',
        answer: 'Yes. Use the Copy Previous Week option in the timesheet actions menu to populate this week\'s grid with the same project rows as last week. You still need to enter this week\'s hours.',
      },
      {
        question: 'What happens if I exceed my contracted weekly hours?',
        answer: 'The timesheet will display a warning indicator but will not prevent submission. Your manager will see the overage during their review.',
      },
    ],
    relatedPages: [
      { label: 'Homepage', path: '/' },
      { label: 'Approvals', path: '/approvals' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
    ],
  },

  '/approvals': {
    pageTitle: 'Approvals',
    description: 'Review, approve, or reject timesheet submissions from your direct reports.',
    icon: '✅',
    functionality: {
      overview:
        'The Approvals page is where managers and administrators review timesheets submitted by their direct reports. You can approve or reject individual timesheets, add comments, and view the complete submission history for each employee.',
      keyFeatures: [
        { label: 'Pending Approvals List', detail: 'Shows all timesheets currently waiting for your action, sorted by submission date.' },
        { label: 'Approve', detail: 'Marks the timesheet as approved and notifies the employee.' },
        { label: 'Reject', detail: 'Returns the timesheet to the employee with a required comment explaining what needs to change.' },
        { label: 'Bulk Actions', detail: 'Allows you to approve or reject multiple timesheets at once when no detailed review is needed.' },
        { label: 'Submission History', detail: 'Shows the full audit trail of a timesheet — who submitted it, when, and any previous approval or rejection actions.' },
        { label: 'Delegation', detail: 'Lets you temporarily delegate your approval authority to another manager while you are unavailable.' },
      ],
      sectionsAndFields: [
        { name: 'Filter Bar', description: 'Filters the list by employee name, date range, or timesheet status.' },
        { name: 'Timesheet List', description: 'A table of submitted timesheets showing employee name, week ending date, total hours, and current status.' },
        { name: 'Employee Name', description: 'The name of the employee who submitted the timesheet.' },
        { name: 'Week Ending', description: 'The last day of the week covered by the timesheet.' },
        { name: 'Total Hours', description: 'The sum of all hours logged across all projects for that week.' },
        { name: 'Status', description: 'The current state of the timesheet: Submitted, Approved, or Rejected.' },
        { name: 'Actions', description: 'Approve and Reject buttons for each timesheet row.' },
        { name: 'Comment Field', description: 'A required text field when rejecting a timesheet — the comment is sent to the employee.' },
      ],
      useCases: [
        'Reviewing and approving a team member\'s timesheet at the end of the week.',
        'Rejecting a timesheet and explaining to the employee what needs correcting.',
        'Approving multiple timesheets in bulk when you have reviewed them already.',
        'Checking the submission history of a specific employee\'s timesheet.',
      ],
    },
    userGuide: {
      gettingStarted:
        'The Approvals page is only accessible to users with manager or admin permissions. If you cannot see Approvals in the menu, your role may not include approval authority — contact your administrator.',
      steps: [
        {
          title: 'How do I approve a timesheet?',
          items: [
            { text: 'Find the timesheet in the Pending Approvals list.' },
            { text: 'Click the timesheet row to open the detail view and review the hours.' },
            { text: 'Click Approve.', note: 'Approval is final unless the employee recalls the timesheet before payroll processing.' },
            { text: 'The employee receives an approval notification automatically.' },
          ],
        },
        {
          title: 'How do I reject a timesheet?',
          items: [
            { text: 'Find the timesheet in the Pending Approvals list.' },
            { text: 'Click the timesheet row to open the detail view.' },
            { text: 'Click Reject.' },
            { text: 'Type a clear explanation in the comment field.', note: 'A comment is required when rejecting. The employee will see this comment and needs it to understand what to correct.' },
            { text: 'Click Confirm Rejection. The timesheet returns to the employee as a Draft.' },
          ],
        },
        {
          title: 'How do I approve multiple timesheets at once?',
          items: [
            { text: 'Select the checkboxes next to the timesheets you want to approve.' },
            { text: 'Click the Bulk Approve button that appears in the action bar.', tip: 'Use bulk approve only when you have already reviewed the timesheets. Individual approval gives you a chance to check the detail first.' },
            { text: 'Confirm the bulk action. All selected timesheets are marked as Approved.' },
          ],
        },
        {
          title: 'How do I set up approval delegation?',
          items: [
            { text: 'Navigate to Approvals → Delegations from the left menu.' },
            { text: 'Click New Delegation.' },
            { text: 'Select the colleague you want to delegate to.' },
            { text: 'Set the start and end dates for the delegation period.' },
            { text: 'Click Save. Your delegate will be notified and can approve timesheets on your behalf during that period.' },
          ],
        },
      ],
      proTips: [
        'Sort by submission date to process the oldest timesheets first and avoid payroll delays.',
        'Use the filter bar to isolate a specific employee\'s timesheets if you manage a large team.',
        'When rejecting, be specific in your comment — vague feedback like "please fix" causes unnecessary back-and-forth.',
        'Check the Submission History panel before approving if a timesheet has been submitted and recalled multiple times.',
      ],
    },
    faq: [
      {
        question: 'Can I approve a timesheet I have already rejected?',
        answer: 'Yes. Once the employee has corrected and re-submitted the timesheet, it will reappear in your Pending Approvals list and you can approve it.',
      },
      {
        question: 'What happens if I do not approve a timesheet before payroll runs?',
        answer: 'Unapproved timesheets are excluded from payroll processing. Check your organisation\'s payroll deadline — timesheets must be approved before that date to be included.',
      },
      {
        question: 'Can I delegate my approval authority to someone else?',
        answer: 'Yes. Navigate to Approvals → Delegations and create a new delegation for the dates you will be unavailable.',
        steps: ['Go to Approvals → Delegations.', 'Click New Delegation.', 'Select the delegate and set the date range.', 'Click Save.'],
      },
      {
        question: 'How do I see timesheets that are already approved?',
        answer: 'Use the Status filter in the filter bar and select Approved. The list will show all previously approved timesheets for your team.',
      },
      {
        question: 'What does the total hours figure include?',
        answer: 'The total hours shown in the approvals list is the sum of all hours logged across every project row in that week\'s timesheet.',
      },
    ],
    relatedPages: [
      { label: 'Homepage', path: '/' },
      { label: 'My Timesheet', path: '/timesheet' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Delegations', path: '/approvals/delegations' },
    ],
  },

  '/reports/timesheet': {
    pageTitle: 'Timesheet Reports',
    description: 'View, filter, and export timesheet data across your team or organisation.',
    icon: '📋',
    functionality: {
      overview:
        'The Timesheet Reports page gives managers and administrators a detailed view of timesheet submissions across employees, projects, and date ranges. You can filter the data, drill down into individual entries, and export results for payroll or project billing.',
      keyFeatures: [
        { label: 'Date Range Filter', detail: 'Restricts the report to timesheets within a specific start and end date.' },
        { label: 'Employee Filter', detail: 'Narrows results to one or more specific employees.' },
        { label: 'Status Filter', detail: 'Filters by timesheet status — Draft, Submitted, Approved, or Rejected.' },
        { label: 'Drill Down', detail: 'Opens the detail view for an individual timesheet directly from the report list.' },
        { label: 'Export', detail: 'Downloads the current filtered view as a CSV or Excel file for use in payroll or billing systems.' },
        { label: 'Summary Totals', detail: 'Shows aggregated hour totals by employee, project, or period at the top of the report.' },
      ],
      sectionsAndFields: [
        { name: 'Filter Bar', description: 'Controls for date range, employee, project, and status filters. Apply filters to narrow the report results.' },
        { name: 'Summary Bar', description: 'Displays total hours for the current filtered view, broken down by status.' },
        { name: 'Report Grid', description: 'A table of timesheet entries matching the current filters, showing employee, week, project, hours, and status.' },
        { name: 'Export Button', description: 'Downloads the current report as a CSV or Excel file.' },
        { name: 'Drilldown Link', description: 'Clicking a row in the report grid opens the full timesheet for that employee and week.' },
      ],
      useCases: [
        'Producing a list of all approved timesheets for a payroll period.',
        'Identifying employees who have not submitted timesheets for a given week.',
        'Checking total hours logged against a specific project across multiple employees.',
        'Exporting timesheet data for import into an external billing or payroll system.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Reports → Timesheet Reports from the left menu. Set your date range first — the report defaults to the current week, which may not show the data you need.',
      steps: [
        {
          title: 'How do I filter the report by date range?',
          items: [
            { text: 'Click the date range control in the filter bar.' },
            { text: 'Select a quick range (e.g. This Month, Last Month) or set a custom start and end date.' },
            { text: 'Click Apply. The report reloads with results for that period.' },
          ],
        },
        {
          title: 'How do I export the report to Excel?',
          items: [
            { text: 'Apply any filters you need to narrow the data.' },
            { text: 'Click the Export button in the top right of the report.' },
            { text: 'Select Excel (.xlsx) or CSV from the format options.', tip: 'Use CSV if you are importing into a payroll system — Excel format preserves formatting but may not be compatible with all import tools.' },
            { text: 'The file downloads automatically to your browser\'s default download location.' },
          ],
        },
        {
          title: 'How do I view an individual timesheet from the report?',
          items: [
            { text: 'Find the row for the employee and week you want to review.' },
            { text: 'Click anywhere on the row to open the timesheet detail view.' },
            { text: 'Review the daily breakdown and project entries.' },
            { text: 'Click Back to return to the report with your filters preserved.' },
          ],
        },
      ],
      proTips: [
        'Combine employee and status filters to quickly find all rejected timesheets for a specific person.',
        'The summary bar totals update in real time as you change filters — use it to spot-check totals before exporting.',
        'Save your most-used filter combinations by bookmarking the URL — filters are reflected in the URL query string.',
        'Run this report at the end of each pay period before approving to catch any missing or draft timesheets.',
      ],
    },
    faq: [
      {
        question: 'How do I see timesheets for the whole organisation, not just my team?',
        answer: 'If you have admin permissions, remove the employee filter and the report shows data for all employees. If you only see your own team, your role is scoped to direct reports only.',
      },
      {
        question: 'Can I export the report in PDF format?',
        answer: 'PDF export is not currently available. Use Excel or CSV export and print from there if a hard copy is needed.',
      },
      {
        question: 'What does the summary bar total include?',
        answer: 'The summary bar shows the total hours for all rows currently visible in the filtered report — it reflects the same data as the export.',
      },
      {
        question: 'How do I find employees who have not submitted their timesheet?',
        answer: 'Set the Status filter to Not Started or Draft and select the relevant date range. The report will show employees who have not yet submitted.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'Missing Timesheets', path: '/reports/missing-timesheets' },
      { label: 'Utilisation Report', path: '/reports/utilisation' },
    ],
  },

  '/reports/missing-timesheets': {
    pageTitle: 'Missing Timesheets',
    description: 'Identify employees who have not submitted a timesheet for a given period.',
    icon: '⚠️',
    functionality: {
      overview:
        'The Missing Timesheets report lists employees who have not submitted a timesheet for one or more weeks in the selected date range. It is used to chase outstanding submissions before payroll closes.',
      keyFeatures: [
        { label: 'Period Filter', detail: 'Sets the date range to check for missing submissions.' },
        { label: 'Employee List', detail: 'Shows which employees are missing timesheets and for which weeks.' },
        { label: 'Send Reminder', detail: 'Sends an automated email reminder to selected employees directly from the report.' },
        { label: 'Export', detail: 'Downloads the missing timesheets list as a CSV for offline follow-up.' },
      ],
      sectionsAndFields: [
        { name: 'Period Selector', description: 'Sets the week or date range to check for missing submissions.' },
        { name: 'Missing Submissions List', description: 'A table of employees and the weeks for which no timesheet has been submitted.' },
        { name: 'Reminder Button', description: 'Sends an email notification to the selected employees reminding them to submit.' },
        { name: 'Export Button', description: 'Downloads the current list as a CSV file.' },
      ],
      useCases: [
        'Identifying which employees have not submitted timesheets before the payroll deadline.',
        'Sending bulk reminders to outstanding submitters in one action.',
        'Producing a record of non-submission for compliance or audit purposes.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Reports → Missing Timesheets. Set the period to the relevant pay period before reviewing the list — the default view shows the current week only.',
      steps: [
        {
          title: 'How do I send a reminder to employees with missing timesheets?',
          items: [
            { text: 'Set the period filter to the relevant week or date range.' },
            { text: 'Select the checkboxes next to the employees you want to remind.' },
            { text: 'Click Send Reminder.', note: 'Reminders are sent via email to the address on the employee\'s profile. Ensure email addresses are up to date in Team Members.' },
            { text: 'A confirmation shows how many reminders were sent.' },
          ],
        },
      ],
      proTips: [
        'Run this report two days before the payroll deadline to give employees enough time to submit.',
        'Use the export to share the missing list with HR or payroll teams who do not have system access.',
      ],
    },
    faq: [
      {
        question: 'What does "missing" mean — does it include Draft timesheets?',
        answer: 'A timesheet is considered missing if it is in Not Started or Draft status. Submitted and Approved timesheets are not included in this report.',
      },
      {
        question: 'Can I send a reminder to a single employee rather than a bulk selection?',
        answer: 'Yes. Select only that employee\'s checkbox before clicking Send Reminder.',
      },
    ],
    relatedPages: [
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Approvals', path: '/approvals' },
      { label: 'Team Members', path: '/team/members' },
    ],
  },

  '/reports/utilisation': {
    pageTitle: 'Utilisation Report',
    description: 'Measure how effectively your team\'s available hours are being used across billable and non-billable work.',
    icon: '📊',
    functionality: {
      overview:
        'The Utilisation Report calculates the percentage of each employee\'s available working hours that have been logged and approved. It helps managers identify over- or under-utilisation and make resourcing decisions.',
      keyFeatures: [
        { label: 'Utilisation Rate', detail: 'Shows the percentage of available hours that are accounted for in approved timesheets.' },
        { label: 'Billable vs Non-Billable Breakdown', detail: 'Separates logged hours into billable and non-billable categories to show the mix of work.' },
        { label: 'Team and Individual Views', detail: 'Toggle between a summary of the whole team and a drill-down for individual employees.' },
        { label: 'Period Comparison', detail: 'Compares utilisation rates across multiple periods to show trends over time.' },
        { label: 'Export', detail: 'Downloads the report data for use in external capacity planning or billing tools.' },
      ],
      sectionsAndFields: [
        { name: 'Period Filter', description: 'Sets the date range for the utilisation calculation.' },
        { name: 'Team Summary', description: 'A bar or table view showing utilisation rate per employee for the selected period.' },
        { name: 'Utilisation %', description: 'Approved hours divided by available hours, expressed as a percentage.' },
        { name: 'Billable Hours', description: 'Hours logged against projects flagged as billable.' },
        { name: 'Non-Billable Hours', description: 'Hours logged against internal or overhead projects.' },
        { name: 'Available Hours', description: 'The employee\'s contracted hours for the selected period, as defined in their working pattern.' },
      ],
      useCases: [
        'Reviewing team utilisation at the end of a month to identify capacity issues.',
        'Checking whether a specific employee is consistently over- or under-allocated.',
        'Producing a utilisation report for client billing reconciliation.',
        'Identifying projects or employees with high non-billable hour ratios.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Reports → Utilisation. The report requires approved timesheets to calculate accurately — utilisation based only on submitted timesheets may understate actual hours if approvals are pending.',
      steps: [
        {
          title: 'How do I view utilisation for a specific month?',
          items: [
            { text: 'Click the Period filter and select the month or set a custom date range.' },
            { text: 'Click Apply. The report recalculates for that period.' },
            { note: 'Utilisation is calculated against approved timesheets only. If a timesheet is still pending approval, those hours will not be included until it is approved.' },
          ],
        },
        {
          title: 'How do I drill down into an individual employee\'s utilisation?',
          items: [
            { text: 'Click the employee\'s row in the team summary view.' },
            { text: 'The individual view opens, showing a week-by-week breakdown of their utilisation for the selected period.' },
          ],
        },
      ],
      proTips: [
        'A utilisation rate above 100% indicates logged hours exceed contracted hours — review for timesheet errors or overtime.',
        'Compare this month against the previous month using the period comparison feature to spot seasonal trends.',
        'Non-billable hours above 20% of total time may indicate inefficiency or misclassification — review project assignments.',
      ],
    },
    faq: [
      {
        question: 'How is the utilisation percentage calculated?',
        answer: 'Utilisation % = (Approved Hours ÷ Available Hours) × 100. Available hours are derived from the employee\'s contracted working pattern for the selected period.',
      },
      {
        question: 'Why does an employee show 0% utilisation when I know they submitted a timesheet?',
        answer: 'The utilisation report uses approved timesheets only. If the timesheet is still in Submitted status and not yet approved, it will not appear in the calculation.',
      },
      {
        question: 'Can I filter the report to show only billable utilisation?',
        answer: 'Yes. Use the filter options on the report to show only billable hours in the utilisation calculation.',
      },
    ],
    relatedPages: [
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Missing Timesheets', path: '/reports/missing-timesheets' },
      { label: 'Team Members', path: '/team/members' },
    ],
  },

  '/team/members': {
    pageTitle: 'Team Members',
    description: 'View and manage the employees in your team, including their roles, working patterns, and contact details.',
    icon: '👥',
    functionality: {
      overview:
        'The Team Members page lists all employees you have visibility of, based on your role. It allows you to view employee details, working patterns, and role assignments, and provides administrators with the ability to add or edit employee records.',
      keyFeatures: [
        { label: 'Employee List', detail: 'Displays all visible employees with name, role, and status at a glance.' },
        { label: 'Employee Detail', detail: 'Opens a full profile for an employee including working pattern, role, and timesheet history.' },
        { label: 'Search and Filter', detail: 'Finds employees quickly by name, role, or department.' },
        { label: 'Working Pattern', detail: 'Shows the contracted hours and working days for each employee, used in utilisation calculations.' },
        { label: 'Role Assignment', detail: 'Associates an employee with a billing rate role for use in cost and utilisation reporting.' },
      ],
      sectionsAndFields: [
        { name: 'Search Bar', description: 'Filters the employee list by name, role, or department as you type.' },
        { name: 'Employee List', description: 'A table of employees showing name, role, department, and active status.' },
        { name: 'Employee Name', description: 'The full name of the employee as it appears in timesheets and reports.' },
        { name: 'Role', description: 'The billing or job role assigned to the employee.' },
        { name: 'Department', description: 'The organisational unit the employee belongs to.' },
        { name: 'Status', description: 'Whether the employee is currently active or inactive in the system.' },
        { name: 'Working Pattern', description: 'The employee\'s contracted days and hours per week, used to calculate available hours in utilisation reports.' },
      ],
      useCases: [
        'Looking up an employee\'s role or working pattern before approving their timesheet.',
        'Checking which employees are currently active in the system.',
        'Updating an employee\'s working pattern after a contract change.',
        'Assigning a billing role to a new employee.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Team → Team Members from the left menu. Managers see their direct reports; administrators see all employees. If an employee is missing from the list, they may be set to inactive or may not yet have a system account.',
      steps: [
        {
          title: 'How do I find a specific employee?',
          items: [
            { text: 'Type the employee\'s name in the search bar at the top of the list.' },
            { text: 'The list filters in real time as you type.' },
            { tip: 'You can also filter by role or department using the filter dropdowns next to the search bar.' },
          ],
        },
        {
          title: 'How do I view an employee\'s working pattern?',
          items: [
            { text: 'Click the employee\'s row to open their profile.' },
            { text: 'Select the Working Pattern tab in the employee detail view.' },
            { text: 'The contracted days and hours per day are listed for each day of the week.' },
          ],
        },
      ],
      proTips: [
        'Working patterns must be set correctly for utilisation reports to calculate accurately — an employee with no working pattern set will show 0% available hours.',
        'Use the Status filter to show only inactive employees if you are auditing who still has system access.',
      ],
    },
    faq: [
      {
        question: 'Can I add a new employee from this page?',
        answer: 'Adding employees depends on your role and whether the system is integrated with an HR platform. Administrators can add employees manually; in integrated environments, employee records are created via the HR system sync.',
      },
      {
        question: 'Why can I not see all employees in the organisation?',
        answer: 'Your view is scoped to the employees you manage or have permission to see. Administrators see all employees. If you need broader access, contact your system administrator.',
      },
      {
        question: 'What does the working pattern affect?',
        answer: 'The working pattern determines the available hours figure used in utilisation calculations. It does not restrict what a user can enter in their timesheet.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'Utilisation Report', path: '/reports/utilisation' },
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
    ],
  },

  '/projects/active': {
    pageTitle: 'Active Projects',
    description: 'View and manage the projects currently available for time logging.',
    icon: '📁',
    functionality: {
      overview:
        'The Active Projects page lists all projects that employees can currently log time against. Administrators use this page to add new projects, update project details, and deactivate projects that are no longer in use.',
      keyFeatures: [
        { label: 'Project List', detail: 'Displays all active projects with their name, code, and status.' },
        { label: 'Project Search', detail: 'Finds a project quickly by name or project code.' },
        { label: 'Add Project', detail: 'Creates a new project and makes it available for time logging.' },
        { label: 'Deactivate', detail: 'Removes a project from the timesheet project search without deleting its historical data.' },
        { label: 'Billable Flag', detail: 'Marks a project as billable or non-billable for use in utilisation and cost reporting.' },
      ],
      sectionsAndFields: [
        { name: 'Search Bar', description: 'Filters the project list by name or project code.' },
        { name: 'Project List', description: 'A table of active projects with name, code, billable status, and actions.' },
        { name: 'Project Name', description: 'The full name of the project as it appears in the timesheet project search.' },
        { name: 'Project Code', description: 'A short unique identifier for the project, used in exports and integrations.' },
        { name: 'Billable', description: 'Indicates whether hours logged to this project count as billable in utilisation reports.' },
        { name: 'Status', description: 'Whether the project is active (available for time logging) or inactive.' },
      ],
      useCases: [
        'Adding a new client project so employees can log time against it.',
        'Deactivating a completed project to remove it from the timesheet project search.',
        'Checking which projects are marked as billable for reporting accuracy.',
        'Finding a project\'s code for use in an external billing system.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Projects → Active Projects from the left menu. You need administrator permissions to add or edit projects. If you cannot see an Add Project button, your role is read-only for this page.',
      steps: [
        {
          title: 'How do I add a new project?',
          items: [
            { text: 'Click the Add Project button in the top right.' },
            { text: 'Enter the project name and a unique project code.' },
            { text: 'Set the Billable flag if hours logged to this project should count as billable.' },
            { text: 'Click Save. The project is immediately available in the timesheet project search.' },
          ],
        },
        {
          title: 'How do I deactivate a project?',
          items: [
            { text: 'Find the project in the list.' },
            { text: 'Click the options menu (three dots) on the project row.' },
            { text: 'Select Deactivate.', note: 'Deactivating a project removes it from the timesheet search but preserves all historical time entries logged against it.' },
            { text: 'Confirm the action. The project moves to the Archived Projects list.' },
          ],
        },
      ],
      proTips: [
        'Use consistent project codes that align with your billing or finance system to simplify data reconciliation.',
        'Mark overhead and internal projects as non-billable from the start to keep utilisation reports clean.',
      ],
    },
    faq: [
      {
        question: 'Can I reactivate a project that has been deactivated?',
        answer: 'Yes. Navigate to Projects → Archived Projects, find the project, and click Reactivate. It will reappear in the timesheet project search.',
      },
      {
        question: 'What happens to historical time entries when I deactivate a project?',
        answer: 'Historical entries are preserved and will still appear in reports. Deactivation only prevents new time from being logged against the project.',
      },
      {
        question: 'How do I change a project\'s billable status?',
        answer: 'Click the project row to open its detail view, toggle the Billable flag, and save. This change applies to future entries — historical data is not recalculated.',
      },
    ],
    relatedPages: [
      { label: 'Timesheet Reports', path: '/reports/timesheet' },
      { label: 'Utilisation Report', path: '/reports/utilisation' },
      { label: 'Archived Projects', path: '/projects/archive' },
    ],
  },

  '/integrations': {
    pageTitle: 'Integrations',
    description: 'Connect TimeTrack Pro to your HR and finance systems to synchronise employee and project data.',
    icon: '⚡',
    functionality: {
      overview:
        'The Integrations page is where administrators configure connections between TimeTrack Pro and external systems such as HR platforms, ERP systems, and payroll tools. Integrations automate the synchronisation of employee records, project data, and approved timesheet exports.',
      keyFeatures: [
        { label: 'Available Integrations', detail: 'Lists the integration connectors available for your instance.' },
        { label: 'Connection Status', detail: 'Shows whether each integration is connected, disconnected, or has errors.' },
        { label: 'Sync Log', detail: 'Displays a history of recent sync operations, including success and failure records.' },
        { label: 'Manual Sync', detail: 'Triggers an immediate data synchronisation without waiting for the scheduled run.' },
        { label: 'Configuration', detail: 'Opens the setup screen for an integration where you can enter API keys, URLs, and field mappings.' },
      ],
      sectionsAndFields: [
        { name: 'Integration Cards', description: 'Each card represents an available connector, showing its name, type, and current connection status.' },
        { name: 'Status Indicator', description: 'A colour-coded badge showing Connected, Disconnected, or Error for each integration.' },
        { name: 'Last Synced', description: 'The timestamp of the most recent successful sync operation.' },
        { name: 'Sync Log', description: 'A list of recent sync events with timestamps and any error messages.' },
        { name: 'Configuration Button', description: 'Opens the integration setup screen to edit connection details.' },
      ],
      useCases: [
        'Connecting an HR system to automatically import new employee records.',
        'Setting up a finance system export to push approved timesheets to payroll.',
        'Diagnosing a sync failure by reviewing the sync log.',
        'Triggering a manual sync after adding new employees in the HR system.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Integrations from the left menu. You need administrator permissions to configure integrations. Before setting up a new integration, have your API credentials and connection details ready from the external system.',
      steps: [
        {
          title: 'How do I connect an integration?',
          items: [
            { text: 'Find the integration card for the system you want to connect.' },
            { text: 'Click Configure.' },
            { text: 'Enter the API key, URL, or credentials provided by the external system.' },
            { text: 'Click Test Connection to verify the credentials are correct.', note: 'If the test connection fails, check that the API key has not expired and that the URL is correct.' },
            { text: 'Click Save. The integration status changes to Connected.' },
          ],
        },
        {
          title: 'How do I trigger a manual sync?',
          items: [
            { text: 'Find the connected integration card.' },
            { text: 'Click Sync Now.' },
            { text: 'The sync log updates in real time. Wait for the Completed status before navigating away.', tip: 'Manual syncs run in the foreground and may take a few minutes for large datasets.' },
          ],
        },
      ],
      proTips: [
        'Schedule automatic syncs to run overnight to avoid performance impacts during business hours.',
        'Review the sync log after each scheduled run to catch errors early — failed records do not retry automatically.',
        'Keep integration credentials in a secure password manager and rotate them according to your security policy.',
      ],
    },
    faq: [
      {
        question: 'What does a sync error mean?',
        answer: 'A sync error means one or more records failed to synchronise. Open the sync log to see which records failed and the reason. Common causes are missing required fields or changed API credentials.',
      },
      {
        question: 'How often does the automatic sync run?',
        answer: 'The sync schedule is configured during integration setup and varies per connector. Check the integration configuration screen for the scheduled frequency.',
      },
      {
        question: 'Can I delete an integration?',
        answer: 'Yes. Open the integration configuration and click Disconnect. This removes the connection but does not delete any data that was already synchronised.',
      },
    ],
    relatedPages: [
      { label: 'Team Members', path: '/team/members' },
      { label: 'Active Projects', path: '/projects/active' },
      { label: 'Settings', path: '/settings/general' },
    ],
  },

  '/approvals/delegations': {
    pageTitle: 'My Delegations',
    description: 'Set up and manage who approves timesheets on your behalf while you are away.',
    icon: '🔁',
    functionality: {
      overview:
        'My Delegations lets you temporarily hand your approval responsibilities to a colleague. When a delegation is active, your delegate receives approval requests in your place for the duration you specify. You can set up multiple delegations and revoke them at any time.',
      keyFeatures: [
        { label: 'Create Delegation', detail: 'Assigns a colleague as your approval delegate for a specific date range.' },
        { label: 'Status Tracking', detail: 'Shows whether each delegation is Active, Expired, or Revoked at a glance.' },
        { label: 'Revoke', detail: 'Cancels an active delegation immediately with an optional reason.' },
        { label: 'Date Range', detail: 'Controls exactly which days the delegation is in effect — useful for holidays or planned absences.' },
        { label: 'Export', detail: 'Downloads your full delegation history to CSV for record-keeping.' },
        { label: 'Status Filter', detail: 'Filters the list to show only Active, Expired, or Revoked delegations.' },
      ],
      sectionsAndFields: [
        { name: 'Set Up Delegation button', description: 'Opens the dialog to create a new delegation.' },
        { name: 'Status Filter', description: 'Dropdown to filter the delegation list by Active, Expired, or Revoked.' },
        { name: 'Delegate', description: 'The colleague who will receive and action approval requests on your behalf.' },
        { name: 'Date Range', description: 'The start and end dates during which the delegation is in effect.' },
        { name: 'Duration', description: 'The number of calendar days the delegation spans.' },
        { name: 'Status', description: 'Current state of the delegation — Active means it is in effect right now.' },
        { name: 'Notes', description: 'Optional free-text field to record the reason for the delegation.' },
        { name: 'Revoke Reason', description: 'The reason entered when a delegation was cancelled early.' },
        { name: 'Revoke button', description: 'Cancels an active delegation immediately.' },
      ],
      useCases: [
        'Setting up annual leave cover so timesheets are not blocked while you are away.',
        'Delegating approvals during a period of reduced availability such as a conference or travel.',
        'Reviewing past delegations to confirm cover was in place for a specific period.',
        'Revoking a delegation early because you have returned sooner than expected.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Approvals → My Delegations from the left menu. You must have the Approvals permission to create delegations. If the Set Up Delegation button is not visible, contact your system administrator to check your role.',
      steps: [
        {
          title: 'How do I set up a delegation?',
          items: [
            { text: 'Click Set Up Delegation in the top right.' },
            { text: 'Select your delegate by typing the first few characters of their name.' },
            { text: 'Set the Start Date and End Date for the delegation period.', note: 'The delegation becomes active at the start of the start date and expires at the end of the end date.' },
            { text: 'Add an optional Note to explain the reason for the delegation.' },
            { text: 'Click Save. The delegation is now active and your delegate will receive approval notifications.' },
          ],
        },
        {
          title: 'How do I revoke a delegation?',
          items: [
            { text: 'Find the active delegation in the list.' },
            { text: 'Click the Revoke button on the right of the row.' },
            { text: 'Enter an optional reason for revoking.' },
            { text: 'Click Confirm. The delegation status changes to Revoked immediately.' },
            { note: 'Revoking a delegation is immediate. Any pending approvals that were routed to your delegate will need to be re-assigned by your administrator.' },
          ],
        },
        {
          title: 'How do I export my delegation history?',
          items: [
            { text: 'Use the Status Filter to select which delegations you want to export (or leave it on All statuses).' },
            { text: 'Click the Export button in the toolbar.' },
            { text: 'A CSV file downloads automatically with all visible delegation records.', tip: 'The filename includes today\'s date for easy filing.' },
          ],
        },
      ],
      proTips: [
        'Set up delegations at least one day before your absence begins so your delegate has time to familiarise themselves.',
        'You can have more than one active delegation at a time — useful if you need two colleagues to share cover.',
        'The delegation does not give your delegate access to your account — it only routes approval requests to them.',
        'Expired delegations remain in the list for audit purposes. Use the Status filter to hide them from the active view.',
      ],
    },
    faq: [
      {
        question: 'Can I delegate to more than one person at the same time?',
        answer: 'Yes. You can create multiple active delegations with overlapping date ranges. All delegates will receive approval notifications during any period where their delegation is active.',
      },
      {
        question: 'What happens to approvals that were sent to my delegate after I revoke the delegation?',
        answer: 'Revoking a delegation stops new approvals being routed to your delegate but does not automatically reassign approvals that were already sent. Contact your administrator to reassign those manually.',
      },
      {
        question: 'Can my delegate see all my timesheets or just the ones needing approval?',
        answer: 'Your delegate only receives approval requests — they do not gain access to your personal timesheet or account settings.',
      },
      {
        question: 'How do I know if my delegation is currently active?',
        answer: 'Active delegations show an Active badge in the Status column. You can use the Status filter to show only active delegations at a glance.',
      },
      {
        question: 'What does "Expired" mean on a delegation?',
        answer: 'Expired means the delegation date range has passed and the delegation ended automatically. No action is needed — expired delegations are kept for audit purposes only.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'Delegation Settings', path: '/settings/approvals/delegation' },
      { label: 'Notifications', path: '/settings/notifications' },
    ],
  },

  '/settings/general': {
    pageTitle: 'General Settings',
    description: 'Configure organisation-wide timesheet rules, working hours, deadlines, and policy enforcement.',
    icon: '⚙️',
    functionality: {
      overview:
        'General Settings is the central configuration page for your organisation\'s timesheet system. Administrators use it to define the working week, submission deadlines, time entry rules, utilisation targets, and policy enforcement behaviour. Changes here affect all users across the organisation.',
      keyFeatures: [
        { label: 'Timesheet Deadlines', detail: 'Sets the day and time by which employees must submit their timesheets each week.' },
        { label: 'Working Hours', detail: 'Defines the standard contracted hours and working days used in utilisation calculations.' },
        { label: 'Time Entry Rules', detail: 'Controls whether roles, cost centres, and descriptions are required on time entries.' },
        { label: 'Utilisation Targets', detail: 'Sets the billable utilisation percentage targets for individuals and teams.' },
        { label: 'Overtime Settings', detail: 'Configures how overtime is classified and whether it requires separate approval.' },
        { label: 'Approval Screen Settings', detail: 'Controls which columns and filters are shown on the Approvals screen.' },
        { label: 'Health Check', detail: 'Runs a diagnostic across all settings to identify conflicts or missing configuration.' },
      ],
      sectionsAndFields: [
        { name: 'Deadline Settings', description: 'The day and time employees must submit by, and the grace period allowed after the deadline.' },
        { name: 'Week Start Day', description: 'The day the working week begins — affects how timesheets are grouped and displayed.' },
        { name: 'Working Hours Configuration', description: 'The contracted hours per day and working days per week used for utilisation.' },
        { name: 'Time Entry Rules', description: 'Toggles for requiring Role, Cost Centre, and Description on each time entry.' },
        { name: 'Project Selection Scope', description: 'Controls whether employees can log time to any project or only projects they are assigned to.' },
        { name: 'Utilisation Settings', description: 'Target billable percentage and the date range used for utilisation calculations.' },
        { name: 'Overtime Settings', description: 'Defines overtime thresholds and whether overtime entries trigger a separate approval workflow.' },
        { name: 'Health Check', description: 'A diagnostic tool that identifies settings conflicts, missing configuration, and policy gaps.' },
      ],
      useCases: [
        'Changing the weekly submission deadline from Friday to Thursday.',
        'Making the Description field mandatory on all time entries.',
        'Setting a 75% billable utilisation target for reporting purposes.',
        'Configuring overtime to require separate manager approval.',
        'Running a health check before a new financial period to catch configuration issues.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings → General from the left menu. You need administrator permissions to make changes. Review existing settings before making changes — some settings affect how historical data is displayed in reports.',
      steps: [
        {
          title: 'How do I change the timesheet submission deadline?',
          items: [
            { text: 'Scroll to the Timesheet Deadlines section.' },
            { text: 'Select the deadline day from the Day dropdown.' },
            { text: 'Set the deadline time using the time field.' },
            { text: 'Optionally set a grace period to allow late submissions within a defined window.' },
            { text: 'Click Save Settings.', note: 'The new deadline applies from the next working week — it does not affect the current open timesheet period.' },
          ],
        },
        {
          title: 'How do I make the Description field mandatory?',
          items: [
            { text: 'Scroll to the Time Entry Rules section.' },
            { text: 'Find the Description requirement toggle.' },
            { text: 'Switch it to Always required.' },
            { text: 'Click Save Settings. Employees will be prompted to enter a description on every time entry going forward.', tip: 'Existing entries are not affected — the rule applies to new entries only.' },
          ],
        },
        {
          title: 'How do I run a Settings Health Check?',
          items: [
            { text: 'Click the Health Check button in the page header.' },
            { text: 'The system scans all settings for conflicts and missing values.' },
            { text: 'Review the findings listed in the Health Check dialog.' },
            { text: 'Click on a finding to navigate directly to the affected setting.' },
            { note: 'The health check is read-only — it does not make any changes automatically.' },
          ],
        },
      ],
      proTips: [
        'Run the Health Check at the start of each financial year to ensure settings are aligned with any policy changes.',
        'If you change the Week Start Day, expect report date groupings to shift — communicate this to your team before making the change.',
        'The Conflict Resolution dialog appears automatically if two settings contradict each other — always resolve conflicts before saving.',
        'Saving settings with validation errors is blocked — scroll to the top to see the error summary if Save is disabled.',
      ],
    },
    faq: [
      {
        question: 'What happens if I change the week start day?',
        answer: 'Changing the week start day affects how timesheets are grouped in the timesheet grid and how weekly deadlines are calculated. Historical data is not recalculated — the change applies to new timesheet periods only.',
      },
      {
        question: 'Can I set different deadlines for different teams?',
        answer: 'General Settings applies one deadline to the whole organisation. Team-specific deadlines are not currently supported.',
      },
      {
        question: 'What does the grace period setting do?',
        answer: 'The grace period allows employees to submit their timesheet after the deadline without triggering a late submission flag. For example, a Friday 5pm deadline with a 2-hour grace period allows submission until 7pm on Friday.',
      },
      {
        question: 'Will changing the utilisation target affect historical reports?',
        answer: 'No. The utilisation target is used for display indicators only (e.g. colour-coded badges). Historical utilisation percentages in reports are based on logged hours and contracted hours, not on the target value.',
      },
    ],
    relatedPages: [
      { label: 'Notifications', path: '/settings/notifications' },
      { label: 'Roles & Rates', path: '/settings/roles' },
      { label: 'Approval Workflows', path: '/settings/approvals' },
      { label: 'Screen Settings', path: '/settings/screens' },
    ],
  },

  '/settings/notifications': {
    pageTitle: 'Notification Settings',
    description: 'Configure automated reminders and digest emails for timesheet submissions, approvals, and missing timesheets.',
    icon: '🔔',
    functionality: {
      overview:
        'Notification Settings controls all automated emails and reminders sent by the system. Administrators configure when reminders are sent, who receives them, and how often digest summaries are delivered to managers. Delegation notifications are also configured here.',
      keyFeatures: [
        { label: 'Timesheet Reminders', detail: 'Sends automated emails to employees who have not submitted their timesheet by a set time before the deadline.' },
        { label: 'Approval Reminders', detail: 'Notifies managers of timesheets that have been waiting for approval beyond a defined threshold.' },
        { label: 'Missing Timesheet Alerts', detail: 'Sends alerts for employees who have not submitted a timesheet at all for a given period.' },
        { label: 'Manager Digest', detail: 'Delivers a scheduled summary of team timesheet status to managers on a defined day and time.' },
        { label: 'Delegation Notifications', detail: 'Controls which emails are sent when delegations are created, activated, or revoked.' },
      ],
      sectionsAndFields: [
        { name: 'Timesheet Reminder Settings', description: 'Enable/disable employee submission reminders and set how many hours before the deadline the reminder is sent.' },
        { name: 'Approval Reminder Settings', description: 'Enable/disable manager approval reminders and set how many hours a submission can wait before a reminder is triggered.' },
        { name: 'Missing Timesheet Reminder', description: 'Enable/disable alerts for employees with no timesheet submitted for the current period.' },
        { name: 'Manager Digest Settings', description: 'Enable/disable the weekly digest email and set the day and time it is delivered to managers.' },
        { name: 'Delegation Notification Settings', description: 'Toggles for notifying delegates when a delegation is created, started, or revoked.' },
        { name: 'Save button', description: 'Commits all notification changes to the system.' },
      ],
      useCases: [
        'Enabling a Friday morning reminder for employees who have not yet submitted their timesheet.',
        'Setting up a manager digest every Monday morning to review the previous week\'s submission rates.',
        'Configuring approval reminders so managers are chased after 24 hours of inaction.',
        'Turning off missing timesheet alerts during a public holiday period to avoid unnecessary noise.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings → Notifications from the left menu. You need administrator permissions to change notification settings. Ensure General Settings deadlines are configured first — notification timings are calculated relative to the submission deadline.',
      steps: [
        {
          title: 'How do I enable timesheet submission reminders?',
          items: [
            { text: 'Scroll to the Timesheet Reminder Settings section.' },
            { text: 'Toggle Enable Timesheet Reminders to on.' },
            { text: 'Set the number of hours before the deadline that the reminder should be sent.' },
            { text: 'Click Save.', note: 'Reminders are sent only to employees who have not yet submitted. Employees who have already submitted will not receive the reminder.' },
          ],
        },
        {
          title: 'How do I set up the manager digest email?',
          items: [
            { text: 'Scroll to the Manager Digest Settings section.' },
            { text: 'Toggle Enable Manager Digest to on.' },
            { text: 'Select the day of the week the digest should be sent.' },
            { text: 'Set the time for delivery.' },
            { text: 'Click Save.', tip: 'Set the digest to arrive on the same day as your team standup so managers have up-to-date submission data to hand.' },
          ],
        },
        {
          title: 'How do I configure delegation notifications?',
          items: [
            { text: 'Scroll to the Delegation Notification Settings section.' },
            { text: 'Toggle each notification type on or off — Delegation Created, Delegation Started, Delegation Revoked.' },
            { text: 'Click Save.' },
          ],
        },
      ],
      proTips: [
        'Set the submission reminder to trigger 4–8 hours before the deadline — early enough for employees to act, late enough that most will have already submitted.',
        'The approval reminder threshold should be shorter than your SLA for approvals — if your policy is 24-hour approval, set the reminder to 18 hours.',
        'Disable missing timesheet alerts for the week of a public holiday to avoid false positives.',
        'Manager digests are only sent if there is at least one team member with an outstanding or submitted timesheet — no email is sent if everything is fully approved.',
      ],
    },
    faq: [
      {
        question: 'Can I set different reminder times for different teams?',
        answer: 'No. Notification settings apply to the whole organisation. Team-specific notification rules are not currently supported.',
      },
      {
        question: 'Will employees receive reminders if their timesheet is already submitted?',
        answer: 'No. Submission reminders are only sent to employees whose timesheet status is Not Started or In Progress at the time the reminder is triggered.',
      },
      {
        question: 'How do I stop all notifications temporarily?',
        answer: 'Toggle off each notification type individually and click Save. There is no single master off switch — each notification type must be disabled separately.',
      },
      {
        question: 'What does the Delegation Started notification do?',
        answer: 'The Delegation Started notification emails the delegate on the first day their delegation becomes active, reminding them that they are now responsible for approvals.',
      },
    ],
    relatedPages: [
      { label: 'General Settings', path: '/settings/general' },
      { label: 'My Delegations', path: '/approvals/delegations' },
      { label: 'Delegation Settings', path: '/settings/approvals/delegation' },
    ],
  },

  '/settings/screens': {
    pageTitle: 'Screen Settings',
    description: 'Control whether each screen in the application uses live data from the API or demo data for testing.',
    icon: '🖥️',
    functionality: {
      overview:
        'Screen Settings allows administrators to toggle individual screens between Live mode (real API data) and Demo mode (pre-loaded sample data). This is useful during onboarding, testing, or demonstrations without affecting production data.',
      keyFeatures: [
        { label: 'Per-Screen Toggle', detail: 'Switches each screen independently between Demo and Live mode.' },
        { label: 'Category Grouping', detail: 'Screens are grouped by functional area (Timesheets, Reports, Settings etc.) for easier navigation.' },
        { label: 'Status Summary', detail: 'Shows a count of how many screens are currently in Demo vs Live mode.' },
        { label: 'Reset All', detail: 'Returns all screens to Demo or Live mode in a single action.' },
        { label: 'Confirmation Dialog', detail: 'Requires confirmation before switching a screen to prevent accidental changes.' },
      ],
      sectionsAndFields: [
        { name: 'Statistics Bar', description: 'Shows the total number of screens and how many are in Demo vs Live mode.' },
        { name: 'Category Groups', description: 'Screens organised by functional area — expand each group to see individual screens.' },
        { name: 'Screen Name', description: 'The display name of the screen as it appears in the application.' },
        { name: 'Mode Toggle', description: 'A switch to move the screen between Demo and Live mode.' },
        { name: 'Current Mode Badge', description: 'Shows the active mode (Demo or Live) for each screen.' },
        { name: 'Reset All button', description: 'Resets every screen to Demo or Live mode in one action.' },
      ],
      useCases: [
        'Switching the Timesheets screen to Live mode when going live with production data.',
        'Keeping Reports screens in Demo mode during a stakeholder demonstration.',
        'Resetting all screens to Demo mode before a training session.',
        'Checking which screens are still in Demo mode before go-live.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings → Screen Settings from the left menu. You need administrator permissions. In Demo mode, screens display pre-loaded sample data — no real data is read or written. In Live mode, screens connect to the real API.',
      steps: [
        {
          title: 'How do I switch a screen to Live mode?',
          items: [
            { text: 'Find the screen in the appropriate category group.' },
            { text: 'Click the mode toggle next to the screen name.' },
            { text: 'Review the confirmation dialog — it shows the screen name and the mode you are switching to.' },
            { text: 'Click Confirm. The badge updates to Live immediately.', note: 'Switching to Live mode means the screen will now read and write real data. Ensure your API connection is configured before switching.' },
          ],
        },
        {
          title: 'How do I reset all screens to Demo mode?',
          items: [
            { text: 'Click the Reset All button in the page header.' },
            { text: 'Select Demo from the options.' },
            { text: 'Confirm the action.', tip: 'Use Reset All to Demo before running a training session to ensure no production data is visible.' },
          ],
        },
      ],
      proTips: [
        'Switch screens to Live mode one at a time during go-live to validate each connection before proceeding to the next.',
        'Demo mode is safe — no data is written to the API, so it is the right mode for training and demonstrations.',
        'The Statistics Bar gives you a quick count of how many screens are Live — use this as a go-live checklist.',
      ],
    },
    faq: [
      {
        question: 'What is the difference between Demo and Live mode?',
        answer: 'Demo mode displays pre-loaded sample data and does not connect to the API — no real data is read or written. Live mode connects to the real API and displays actual production data.',
      },
      {
        question: 'Can I switch screens to Live mode one at a time?',
        answer: 'Yes. Each screen has an independent toggle. You can switch screens to Live mode individually, which is the recommended approach during a phased go-live.',
      },
      {
        question: 'What happens to changes I made in Demo mode when I switch to Live?',
        answer: 'Changes made in Demo mode are not saved to the API — they are lost when you switch to Live mode. Demo mode data resets each session.',
      },
      {
        question: 'Will users see a difference when I switch a screen from Demo to Live?',
        answer: 'Yes. The data displayed will change from sample data to real data immediately. Inform users before switching screens to Live during business hours.',
      },
    ],
    relatedPages: [
      { label: 'General Settings', path: '/settings/general' },
      { label: 'Integrations', path: '/integrations' },
    ],
  },

  '/settings/roles': {
    pageTitle: 'Roles & Rates',
    description: 'Manage the billing roles and charge-out rates used in timesheets, cost reporting, and utilisation calculations.',
    icon: '💼',
    functionality: {
      overview:
        'The Roles & Rates page is where administrators define the job roles that employees can be assigned to, along with the charge-out rates associated with each role. Roles are used in timesheet entries, project cost reporting, and utilisation calculations. Rate overrides can be set at the project or employee level.',
      keyFeatures: [
        { label: 'Role List', detail: 'Displays all active and archived roles with their name, code, and default rate.' },
        { label: 'Add Role', detail: 'Creates a new billing role with a name, code, and default charge-out rate.' },
        { label: 'Edit Role', detail: 'Updates a role\'s name, code, or rate — changes apply to future entries.' },
        { label: 'Archive Role', detail: 'Removes a role from active use while preserving historical data.' },
        { label: 'Project Overrides', detail: 'Sets a different charge-out rate for a specific role on a specific project.' },
        { label: 'Employee Overrides', detail: 'Sets a different charge-out rate for a specific employee regardless of their role.' },
      ],
      sectionsAndFields: [
        { name: 'Roles tab', description: 'Lists all billing roles with name, code, default rate, and active status.' },
        { name: 'Project Overrides tab', description: 'Lists project-specific rate overrides — where a role is billed at a different rate on a particular project.' },
        { name: 'Employee Overrides tab', description: 'Lists employee-specific rate overrides — where an individual is billed at a rate different from their role default.' },
        { name: 'Role Name', description: 'The display name of the role as it appears in timesheet entries and reports.' },
        { name: 'Role Code', description: 'A short identifier used in exports and integrations.' },
        { name: 'Default Rate', description: 'The standard charge-out rate for this role in the organisation\'s base currency.' },
        { name: 'Show Archived toggle', description: 'Reveals archived roles in the list for reference or reactivation.' },
      ],
      useCases: [
        'Adding a new Senior Developer role with a revised charge-out rate.',
        'Setting a discounted project rate for a specific client project.',
        'Archiving a role that is no longer used without losing historical billing data.',
        'Checking the rate override applied to a specific employee before raising an invoice.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings → Roles & Rates from the left menu. You need administrator permissions. Before adding roles, agree on a consistent naming convention — role names appear in timesheet dropdowns and client-facing reports.',
      steps: [
        {
          title: 'How do I add a new role?',
          items: [
            { text: 'Click Add Role in the top right.' },
            { text: 'Enter a Role Name — use a clear, consistent naming convention.' },
            { text: 'Enter a Role Code — a short unique identifier (e.g. SR-DEV).' },
            { text: 'Set the Default Rate in the organisation\'s base currency.' },
            { text: 'Click Save. The role is immediately available for assignment to employees and timesheet entries.' },
          ],
        },
        {
          title: 'How do I set a project-specific rate override?',
          items: [
            { text: 'Click the Project Overrides tab.' },
            { text: 'Click Add Override.' },
            { text: 'Select the Project and the Role.' },
            { text: 'Enter the Override Rate.' },
            { text: 'Click Save.', note: 'The project override takes precedence over the default role rate and any employee override for that project.' },
          ],
        },
        {
          title: 'How do I archive a role that is no longer used?',
          items: [
            { text: 'Find the role in the Roles tab.' },
            { text: 'Click the options menu on the role row.' },
            { text: 'Select Archive.' },
            { text: 'Confirm the action.', note: 'Archived roles are hidden from timesheet dropdowns but historical data is preserved. Enable Show Archived to see them.' },
          ],
        },
      ],
      proTips: [
        'Keep role codes short and aligned with your billing system codes to simplify invoice reconciliation.',
        'Use project overrides rather than creating separate roles for client-specific rates — it keeps the role list clean.',
        'Review archived roles periodically — sometimes a role needs to be reactivated rather than recreated.',
        'Employee overrides should be the exception, not the rule — if multiple employees share the same rate, create a role for it instead.',
      ],
    },
    faq: [
      {
        question: 'What happens to historical timesheet data if I change a role\'s rate?',
        answer: 'Changing the default rate on a role does not recalculate historical billing data. The new rate applies to future timesheet entries only.',
      },
      {
        question: 'Can I have more than one rate for the same role?',
        answer: 'Yes — use Project Overrides to apply a different rate for a specific role on a specific project, or Employee Overrides to set a rate for an individual person.',
      },
      {
        question: 'How do I reactivate an archived role?',
        answer: 'Enable the Show Archived toggle to see archived roles. Click the options menu on the archived role and select Reactivate. The role becomes available in timesheet dropdowns again.',
      },
      {
        question: 'What does the Role Code field do?',
        answer: 'The Role Code is a short identifier used in CSV exports and integrations. It does not appear to employees in the timesheet — it is for administrator and system use only.',
      },
    ],
    relatedPages: [
      { label: 'Team Members', path: '/team/members' },
      { label: 'General Settings', path: '/settings/general' },
      { label: 'Utilisation Report', path: '/reports/utilisation' },
    ],
  },

  '/settings/roles-permissions': {
    pageTitle: 'User Roles & Permissions',
    description: 'Define and manage the permission sets that control what each user can see and do in the application.',
    icon: '🛡️',
    functionality: {
      overview:
        'User Roles & Permissions is where administrators create and manage the permission profiles assigned to users. Each role defines which pages, actions, and data a user can access. Roles can be duplicated to create variants and deleted when no longer needed.',
      keyFeatures: [
        { label: 'Role List', detail: 'Displays all defined permission roles with their name, description, and the number of users assigned.' },
        { label: 'Create Role', detail: 'Defines a new permission profile from scratch or as a copy of an existing role.' },
        { label: 'Edit Role', detail: 'Modifies which permissions are granted by an existing role.' },
        { label: 'Duplicate Role', detail: 'Creates an exact copy of an existing role as a starting point for a new variant.' },
        { label: 'Delete Role', detail: 'Removes a role that is no longer needed — only possible if no users are currently assigned to it.' },
        { label: 'Search', detail: 'Filters the role list by name for quick lookup in organisations with many roles.' },
      ],
      sectionsAndFields: [
        { name: 'Search Bar', description: 'Filters the role list by role name as you type.' },
        { name: 'Role Name', description: 'The display name of the permission profile.' },
        { name: 'Description', description: 'A plain-language summary of what the role is intended for.' },
        { name: 'Users Assigned', description: 'The number of users currently assigned to this role.' },
        { name: 'Create Role button', description: 'Opens the role editor to define a new permission profile.' },
        { name: 'Duplicate button', description: 'Creates a copy of the selected role with all the same permissions.' },
        { name: 'Delete button', description: 'Removes the role permanently — only enabled if no users are assigned.' },
      ],
      useCases: [
        'Creating a read-only Report Viewer role for finance staff who need to see reports but not manage timesheets.',
        'Duplicating the Manager role to create a Senior Manager role with additional admin permissions.',
        'Auditing which roles exist before assigning a new employee to the system.',
        'Deleting a legacy role that was replaced by a newer profile.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings → User Roles & Permissions from the left menu. You need administrator permissions. Plan your role structure before creating roles — it is easier to design roles correctly from the start than to reassign users later.',
      steps: [
        {
          title: 'How do I create a new role?',
          items: [
            { text: 'Click Create Role in the top right.' },
            { text: 'Enter a Role Name and Description.' },
            { text: 'Configure the permissions in each category — toggle individual permissions on or off.' },
            { text: 'Click Save. The role is immediately available for assignment to users.', tip: 'Use the Duplicate function on an existing role if you want to start from a known configuration rather than from scratch.' },
          ],
        },
        {
          title: 'How do I duplicate a role?',
          items: [
            { text: 'Find the role you want to copy in the list.' },
            { text: 'Click the Duplicate button on the role row.' },
            { text: 'Confirm the duplication.', note: 'The duplicate is created with the same permissions and the name "Copy of [Role Name]". Rename it immediately to avoid confusion.' },
            { text: 'Click the Edit button on the new copy to rename it and adjust permissions.' },
          ],
        },
        {
          title: 'How do I delete a role?',
          items: [
            { text: 'Ensure no users are assigned to the role — the Users Assigned column must show 0.' },
            { text: 'Click the Delete button on the role row.' },
            { text: 'Confirm the deletion.', note: 'If users are still assigned to the role, the Delete button is disabled. Reassign those users first.' },
          ],
        },
      ],
      proTips: [
        'Keep roles aligned with job functions, not individual people — avoid creating a "John Smith" role.',
        'Use the Description field to document who the role is designed for and what its key permissions are.',
        'Review the Users Assigned count regularly — roles with zero users assigned may be candidates for deletion.',
        'Start with the minimum permissions needed and add more if users report they cannot complete a task.',
      ],
    },
    faq: [
      {
        question: 'Can I delete a role that has users assigned to it?',
        answer: 'No. The Delete button is disabled if any users are currently assigned to the role. Reassign all users to another role first, then delete.',
      },
      {
        question: 'What happens to a user\'s access if I edit the role they are assigned to?',
        answer: 'Permission changes take effect immediately. The user may need to refresh their browser to see the updated access.',
      },
      {
        question: 'How do I assign a role to a user?',
        answer: 'Role assignment is done from the Team Members page — open the employee record and update their assigned role.',
      },
      {
        question: 'Can a user have more than one role?',
        answer: 'Each user is assigned one role at a time. If a user needs access from two different roles, create a combined role that includes all the required permissions.',
      },
    ],
    relatedPages: [
      { label: 'Team Members', path: '/team/members' },
      { label: 'General Settings', path: '/settings/general' },
      { label: 'Roles & Rates', path: '/settings/roles' },
    ],
  },

  '/settings/approvals': {
    pageTitle: 'Approval Workflows',
    description: 'Create and manage the multi-stage approval workflows that control how timesheets are reviewed and approved.',
    icon: '✅',
    functionality: {
      overview:
        'Approval Workflows is where administrators design the approval chains used across the organisation. Workflows define how many stages a timesheet must pass through, who approves at each stage, and the rules for escalation. Once created, workflows are assigned to teams or individuals.',
      keyFeatures: [
        { label: 'Workflow List', detail: 'Shows all defined workflows with their name, number of stages, status, and number of assigned employees.' },
        { label: 'Create Workflow', detail: 'Opens a step-by-step wizard to define a new approval chain.' },
        { label: 'Workflow Details', detail: 'Shows the full stage breakdown for a selected workflow.' },
        { label: 'Assign Workflow', detail: 'Links a workflow to specific employees or teams.' },
        { label: 'Duplicate Workflow', detail: 'Creates a copy of an existing workflow as a starting point.' },
        { label: 'Status Control', detail: 'Sets a workflow to Active, Draft, or Inactive.' },
      ],
      sectionsAndFields: [
        { name: 'Search Bar', description: 'Filters workflows by name.' },
        { name: 'Status Filter', description: 'Shows only Active, Draft, or Inactive workflows.' },
        { name: 'Workflow Name', description: 'The display name of the workflow.' },
        { name: 'Stages', description: 'The number of sequential approval stages in the workflow.' },
        { name: 'Assigned Employees', description: 'How many employees are currently using this workflow.' },
        { name: 'Status Badge', description: 'Whether the workflow is Active, Draft, or Inactive.' },
        { name: 'Create Workflow button', description: 'Opens the workflow creation wizard.' },
        { name: 'Assign button', description: 'Opens the dialog to link the workflow to employees.' },
      ],
      useCases: [
        'Creating a two-stage workflow where a line manager approves first and finance approves second.',
        'Assigning the standard workflow to all new employees during onboarding.',
        'Duplicating the standard workflow to create a simplified one-stage version for contractors.',
        'Deactivating a workflow that was used for a project that has now ended.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings → Approvals from the left menu. You need administrator permissions. Before creating workflows, identify who the approvers will be at each stage — approvers must already have a manager or admin role in the system.',
      steps: [
        {
          title: 'How do I create a new approval workflow?',
          items: [
            { text: 'Click Create Workflow in the top right.' },
            { text: 'Enter a Workflow Name and optional description.' },
            { text: 'Add stages using the Add Stage button. For each stage, select the approver or approver group.' },
            { text: 'Set the stage order — stage 1 receives the submission first, then stage 2 and so on.' },
            { text: 'Click Save. The workflow is created in Draft status.', tip: 'Test a new workflow by assigning it to yourself first before rolling it out to the team.' },
            { text: 'Change the status to Active when you are ready to use it.' },
          ],
        },
        {
          title: 'How do I assign a workflow to employees?',
          items: [
            { text: 'Find the workflow in the list.' },
            { text: 'Click the Assign button on the workflow row.' },
            { text: 'Select the employees or teams to assign.' },
            { text: 'Click Save.', note: 'Each employee can only be assigned one active workflow at a time. Assigning a new workflow replaces the previous one.' },
          ],
        },
        {
          title: 'How do I duplicate a workflow?',
          items: [
            { text: 'Click the options menu on the workflow row.' },
            { text: 'Select Duplicate.' },
            { text: 'The copy is created as a Draft with the name "Copy of [Workflow Name]".' },
            { text: 'Click the workflow to open its details and make the necessary changes before activating.' },
          ],
        },
      ],
      proTips: [
        'Keep workflows as simple as possible — each additional stage increases the time to approval.',
        'Use Draft status while designing a workflow so it cannot be accidentally assigned to employees.',
        'Review Assigned Employees counts regularly — workflows with zero assigned employees may be obsolete.',
        'If an approver leaves the organisation, update all workflows that reference them before deactivating their account.',
      ],
    },
    faq: [
      {
        question: 'What happens to timesheets in progress if I change a workflow?',
        answer: 'Timesheets already in the approval process continue on the original workflow. The updated workflow applies to new submissions only.',
      },
      {
        question: 'Can I have different workflows for different teams?',
        answer: 'Yes. Create separate workflows for each team and assign them accordingly. Each employee follows the workflow assigned to them.',
      },
      {
        question: 'What is the difference between Draft and Inactive?',
        answer: 'Draft means the workflow is being designed and has never been used. Inactive means the workflow was previously active but has been decommissioned. Both statuses prevent the workflow from being assigned to new employees.',
      },
      {
        question: 'Can an approver approve their own timesheet?',
        answer: 'No. If an approver is also a timesheet submitter, the system skips them in the approval chain and routes to the next stage approver. This is enforced automatically.',
      },
    ],
    relatedPages: [
      { label: 'Approvals', path: '/approvals' },
      { label: 'My Delegations', path: '/approvals/delegations' },
      { label: 'Delegation Settings', path: '/settings/approvals/delegation' },
      { label: 'User Roles & Permissions', path: '/settings/roles-permissions' },
    ],
  },

  '/settings/approvals/delegation': {
    pageTitle: 'Delegation Settings',
    description: 'Configure the organisation-wide rules that govern how approval delegation works for all users.',
    icon: '🔐',
    functionality: {
      overview:
        'Delegation Settings gives administrators control over every aspect of the delegation feature at an organisational level. Settings here define who can delegate, how long delegations can last, whether delegates can further delegate, and which notifications are triggered. Changes apply to all users immediately.',
      keyFeatures: [
        { label: 'Enable/Disable Delegation', detail: 'Turns the delegation feature on or off for the entire organisation.' },
        { label: 'Max Delegation Duration', detail: 'Sets the maximum number of days a delegation can last.' },
        { label: 'Max Concurrent Delegations', detail: 'Limits how many active delegations a user can have at the same time.' },
        { label: 'Advance Notice Requirement', detail: 'Requires delegations to be set up a minimum number of days before they start.' },
        { label: 'Chain Delegation Control', detail: 'Controls whether a delegate is permitted to delegate further to another person.' },
        { label: 'Notification Toggles', detail: 'Enables or disables each type of delegation email notification independently.' },
        { label: 'Audit Trail', detail: 'Links to the delegation audit report showing all delegation activity across the organisation.' },
      ],
      sectionsAndFields: [
        { name: 'Delegation Enabled toggle', description: 'Master switch to turn delegation on or off for all users.' },
        { name: 'Max Duration (days)', description: 'The longest period any single delegation can cover.' },
        { name: 'Max Concurrent Delegations', description: 'How many active delegations one user can have simultaneously.' },
        { name: 'Advance Notice (days)', description: 'How many days in advance a delegation must be set up before it starts.' },
        { name: 'Allow Chain Delegation toggle', description: 'Whether a delegate can delegate to a third person.' },
        { name: 'Require Admin Approval toggle', description: 'Whether a new delegation requires administrator sign-off before becoming active.' },
        { name: 'Notification Settings', description: 'Independent toggles for Created, Started, Expiring Soon, and Revoked notification emails.' },
        { name: 'Save button', description: 'Commits all changes to the organisation settings.' },
      ],
      useCases: [
        'Disabling chain delegation to prevent approval chains becoming too long or unaccountable.',
        'Setting a 30-day maximum duration to prevent permanent delegation arrangements.',
        'Requiring administrator approval for all new delegations in a regulated environment.',
        'Turning off delegation entirely during a system migration period.',
      ],
    },
    userGuide: {
      gettingStarted:
        'Navigate to Settings → Approvals and select Delegation Settings, or go directly via Settings → Approvals → Delegation. You need administrator permissions. Review the current settings before making changes — some settings such as disabling delegation will immediately affect users who have active delegations.',
      steps: [
        {
          title: 'How do I disable the delegation feature?',
          items: [
            { text: 'Toggle Delegation Enabled to off.' },
            { text: 'Click Save.', note: 'Disabling delegation does not automatically revoke active delegations. Active delegations will continue until they expire. Inform affected users before disabling.' },
          ],
        },
        {
          title: 'How do I set a maximum delegation duration?',
          items: [
            { text: 'Find the Max Duration field.' },
            { text: 'Enter the maximum number of calendar days allowed for any single delegation.' },
            { text: 'Click Save.', note: 'This setting applies to new delegations only. Existing delegations are not truncated.' },
          ],
        },
        {
          title: 'How do I require administrator approval for delegations?',
          items: [
            { text: 'Toggle Require Admin Approval to on.' },
            { text: 'Click Save.' },
            { text: 'From this point, all new delegations will enter a Pending Approval state until an administrator approves them.', tip: 'Ensure at least two administrators are active so delegation approvals are not blocked if one is unavailable.' },
          ],
        },
        {
          title: 'How do I view the full delegation audit trail?',
          items: [
            { text: 'Click the View Audit Log button on the Delegation Settings page.' },
            { text: 'The Delegation Audit report opens, showing all delegation activity across the organisation with timestamps and actor names.' },
          ],
        },
      ],
      proTips: [
        'Set the advance notice requirement to at least 1 day to prevent last-minute delegations that give delegates insufficient preparation time.',
        'Disable chain delegation unless your organisation specifically requires it — it simplifies the approval audit trail considerably.',
        'Review the audit log quarterly to check for unusual delegation patterns such as the same person being delegated to repeatedly.',
        'If you enable Require Admin Approval, set up a shared inbox or notification so approval requests are not missed.',
      ],
    },
    faq: [
      {
        question: 'What is chain delegation?',
        answer: 'Chain delegation is when a delegate creates their own delegation to pass approval responsibilities to a third person. Disabling chain delegation means a delegate must handle the approvals themselves.',
      },
      {
        question: 'What happens to active delegations if I reduce the Max Duration setting?',
        answer: 'Existing active delegations are not affected by changing the Max Duration — they continue until their original end date. The new limit applies to delegations created after the change.',
      },
      {
        question: 'Can I see all delegations across the organisation in one place?',
        answer: 'Yes. Click View Audit Log from the Delegation Settings page to open the Delegation Audit report, which shows all delegation activity across all users.',
      },
      {
        question: 'How does Require Admin Approval affect users?',
        answer: 'When enabled, a delegation created by a user enters Pending Approval status and is not active until an administrator approves it. The delegate does not receive approval responsibilities until the delegation is approved.',
      },
    ],
    relatedPages: [
      { label: 'My Delegations', path: '/approvals/delegations' },
      { label: 'Approval Workflows', path: '/settings/approvals' },
      { label: 'Notifications', path: '/settings/notifications' },
      { label: 'Approvals', path: '/approvals' },
    ],
  },
};

// Helper: look up help content for a given path.
// Returns null if no content exists for that path.
export const getHelpContent = (path: string): HelpContent | null => {
  return helpContent[path] ?? null;
};
